<template>
  <div class="d-flex flex-column gap-4">
    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="fw-bold mb-0">Dados do Atendimento</h6>
          <button type="button"
                  class="btn btn-sm btn-light-primary"
                  @click="toggleSection('procedureDetails')"
                  :data-bs-toggle="'collapse'"
                  :data-bs-target="'#procedureDetailsCollapse'">
            <i :class="expandedSections.procedureDetails ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
            <span class="ms-1">{{ expandedSections.procedureDetails ? 'Ocultar' : 'Mostrar' }} detalhes</span>
          </button>
        </div>
        <div class="row">
          <div class="col-md-3">
            <label class="fs-6 fw-bold mb-2">Data do atendimento</label>
            <input
              type="date"
              class="form-control form-control-solid"
              v-model="form.attendance_date"
              placeholder="DD/MM/YYYY"
            />
            <small class="text-muted">Opcional.</small>
          </div>
          <div class="col-md-4">
            <label class="fs-6 fw-bold mb-2 required">Código do Procedimento</label>
            <input
              v-model="form.procedure_code"
              type="text"
              class="form-control form-control-solid"
              maxlength="20"
              placeholder="Ex: 10101012"
              required
            >
          </div>
          <div class="col-md-3">
            <label class="fs-6 fw-bold mb-2 required">Valor do Procedimento</label>
            <money3
              v-model="form.procedure_value"
              class="form-control form-control-solid"
              required
              v-bind="money3Config"
            />
          </div>
          <div class="col-md-2">
            <label class="fs-6 fw-bold mb-2 required">Valor para Repasse</label>
            <money3
              v-model="form.procedure_transfer_value"
              class="form-control form-control-solid"
              required
              v-bind="money3Config"
            />
          </div>
        </div>
        <div v-if="form.procedure_code || form.procedure_value || form.procedure_transfer_value" class="mt-2">
          <small class="text-muted">
            <i class="bi bi-file-earmark-medical"></i> Código: {{ form.procedure_code || 'Não informado' }}
            <span v-if="form.procedure_value" class="ms-2"><i class="bi bi-currency-dollar"></i> R$ {{ formatCurrency(form.procedure_value) }}</span>
            <span v-if="form.procedure_transfer_value" class="ms-2"><i class="bi bi-cash-stack"></i> Repasse: R$ {{ formatCurrency(form.procedure_transfer_value) }}</span>
          </small>
        </div>
        <div class="collapse" :class="{ show: expandedSections.procedureDetails }" id="procedureDetailsCollapse">
          <div class="row mt-2">
            <div class="col-md-3">
              <label class="fs-6 fw-bold mb-2">Tabela</label>
              <input v-model="form.procedure_table"
                     type="text"
                     class="form-control form-control-solid"
                     maxlength="10"
                     placeholder="22">
              <small class="text-muted">Padrão: 22 (TISS)</small>
            </div>
            <div class="col-md-3">
              <label class="fs-6 fw-bold mb-2">Data de Emissão</label>
              <input
                type="date"
                class="form-control form-control-solid"
                v-model="form.emission_date"
                placeholder="DD/MM/YYYY"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="fw-bold mb-0">Dados Específicos - Consulta</h6>
          <button type="button"
                  class="btn btn-sm btn-light-primary"
                  @click="toggleSection('consultationDetails')"
                  :data-bs-toggle="'collapse'"
                  :data-bs-target="'#consultationDetailsCollapse'">
            <i :class="expandedSections.consultationDetails ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
            <span class="ms-1">{{ expandedSections.consultationDetails ? 'Ocultar' : 'Mostrar' }} detalhes</span>
          </button>
        </div>
        <div v-if="form.consultation_type || form.attendance_regime" class="mb-2">
          <small class="text-muted">
            <span v-if="form.consultation_type">
              <i class="bi bi-calendar-check"></i> Tipo: {{ consultationTypes.find(t => t.tiss_value === form.consultation_type)?.tiss_description || form.consultation_type }}
            </span>
            <span v-if="form.attendance_regime" class="ms-2">
              <i class="bi bi-clock"></i> Regime: {{ attendanceRegimes.find(r => r.code === form.attendance_regime)?.description || form.attendance_regime }}
            </span>
          </small>
        </div>
        <div class="collapse" :class="{ show: expandedSections.consultationDetails }" id="consultationDetailsCollapse">
          <div class="row">
            <div class="col-md-6">
              <label class="fs-6 fw-bold mb-2">Tipo de Consulta</label>
              <Dropdown
                :options="consultationTypes"
                v-model="form.consultation_type"
                optionLabel="tiss_description"
                optionValue="tiss_value"
                :filter="false"
                empty-message="Nenhuma opção"
                class="form-control form-control-solid w-full md:w-14rem form-control-sm"
                placeholder="Selecione o tipo"
                :showClear="true"
                style="display: flex; padding: 0.35rem 0.75rem"
              />
            </div>
            <div class="col-md-6">
              <label class="fs-6 fw-bold mb-2">Indicador de Acidente</label>
              <Dropdown
                v-model="form.accident_indicator"
                :options="accidentIndicators"
                optionLabel="description"
                optionValue="code"
                placeholder="Selecione o indicador"
                class="w-100"
                :showClear="true"
                style="display: flex; padding: 0.35rem 0.75rem"
              />
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-4">
              <label class="fs-6 fw-bold mb-2">Atendimento a Recém-Nascido</label>
              <select v-model="form.newborn_care" class="form-control form-control-solid">
                <option value="N">Não</option>
                <option value="S">Sim</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="fs-6 fw-bold mb-2">Cobertura Especial</label>
              <input v-model="form.special_coverage"
                     type="text"
                     class="form-control form-control-solid"
                     maxlength="10"
                     placeholder="Cobertura especial">
            </div>
            <div class="col-md-4">
              <label class="fs-6 fw-bold mb-2">Regime de Atendimento</label>
              <Dropdown
                :options="attendanceRegimes"
                v-model="form.attendance_regime"
                optionLabel="description"
                optionValue="code"
                :filter="true"
                appendTo="self"
                empty-message="Nenhuma opção"
                empty-filter-message="Nenhuma opção encontrada"
                class="form-control form-control-solid w-full md:w-14rem form-control-sm"
                placeholder="Selecione o regime"
                :showClear="true"
                style="display: flex; padding: 0.35rem 0.75rem"
              />
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-4">
              <label class="fs-6 fw-bold mb-2">Saúde Ocupacional</label>
              <select v-model="form.occupational_health" class="form-control form-control-solid">
                <option value="">Não informado</option>
                <option value="N">Não</option>
                <option value="S">Sim</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from 'primevue/dropdown';
import { Money3Component } from 'v-money3';

export default {
  name: 'TissConsultationGuideForm',
  components: { Dropdown, money3: Money3Component },
  props: {
    form: { type: Object, required: true },
    expandedSections: { type: Object, required: true },
    consultationTypes: { type: Array, default: () => [] },
    attendanceRegimes: { type: Array, default: () => [] },
    accidentIndicators: { type: Array, default: () => [] },
    formatCurrency: { type: Function, required: true },
  },
  data() {
    return {
      money3Config: {
        masked: false,
        prefix: 'R$ ',
        suffix: '',
        thousands: '.',
        decimal: ',',
        precision: 2,
        disableNegative: true,
        disabled: false,
        min: null,
        max: null,
        allowBlank: false,
        minimumNumberOfCharacters: 0,
        shouldRound: false,
        focusOnRight: false,
      },
    };
  },
  methods: {
    toggleSection(key) {
      this.expandedSections[key] = !this.expandedSections[key];
    },
  },
};
</script>
